import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ─── Workout Adherence Card ───────────────────────────────────────────────────
const WorkoutDayCard = ({ day, completions, onToggle, onEditNote }) => {
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingExId, setEditingExId] = useState(null);
  const [noteText, setNoteText] = useState('');

  const exercises = day?.workoutPlanExercises || [];
  const isRestDay = day?.isRest || exercises?.length === 0;
  const completedCount = exercises?.filter(ex => completions?.[ex?.id]?.completed)?.length;
  const isToday = day?.dayName === DAYS?.[new Date()?.getDay() === 0 ? 6 : new Date()?.getDay() - 1];

  const openNote = (exId) => {
    setEditingExId(exId);
    setNoteText(completions?.[exId]?.notes || '');
    setShowNoteModal(true);
  };

  const saveNote = () => {
    if (editingExId) onEditNote(editingExId, noteText);
    setShowNoteModal(false);
    setEditingExId(null);
    setNoteText('');
  };

  return (
    <div className={`bg-card border rounded-2xl overflow-hidden transition-all ${isToday ? 'border-primary/50 shadow-lg shadow-primary/10' : isRestDay ? 'border-border/50' : 'border-border'}`}>
      {/* Day Header */}
      <div className={`flex items-center justify-between px-4 py-3 ${isToday ? 'bg-primary/10' : isRestDay ? 'bg-muted/20' : 'bg-muted/30'}`}>
        <div className="flex items-center gap-2">
          {isToday && <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
          <div>
            <p className="text-sm font-bold text-foreground">{day?.dayName}</p>
            {day?.scheduled_date && (
              <p className="text-xs text-muted-foreground">{new Date(day?.scheduled_date + 'T12:00:00')?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isRestDay ? (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground flex items-center gap-1">
              <Icon name="Moon" size={10} />
              Rest
            </span>
          ) : (
            <>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${completedCount === exercises?.length && exercises?.length > 0 ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'}`}>
                {completedCount}/{exercises?.length}
              </span>
            </>
          )}
          {isToday && <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">Today</span>}
        </div>
      </div>
      {/* Exercises */}
      <div className="p-3 space-y-2">
        {isRestDay ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Icon name="Moon" size={22} className="text-muted-foreground/50 mb-2" />
            <p className="text-xs font-semibold text-muted-foreground">Rest Day</p>
            <p className="text-xs text-muted-foreground/60 mt-0.5">Recovery & relaxation</p>
          </div>
        ) : (
          exercises?.map((ex, idx) => {
            const completion = completions?.[ex?.id];
            const done = completion?.completed;
            return (
              <div key={ex?.id} className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all ${done ? 'bg-success/5 border-success/20' : 'bg-background border-border'}`}>
                <button
                  onClick={() => onToggle(ex?.id, !done)}
                  className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${done ? 'bg-success border-success text-white' : 'border-border hover:border-primary'}`}
                >
                  {done && <Icon name="Check" size={12} />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                    {ex?.exercise?.name || `Exercise ${idx + 1}`}
                  </p>
                  <p className="text-xs text-muted-foreground">{ex?.sets}×{ex?.reps} reps · {ex?.rest_seconds}s rest</p>
                  {completion?.notes && <p className="text-xs text-primary/70 mt-0.5 italic truncate">"{completion?.notes}"</p>}
                </div>
                <button
                  onClick={() => openNote(ex?.id)}
                  className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                  title="Add note"
                >
                  <Icon name="MessageSquare" size={13} />
                </button>
              </div>
            );
          })
        )}
      </div>
      {/* Note Modal */}
      <AnimatePresence>
        {showNoteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowNoteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card border border-border rounded-2xl p-5 w-full max-w-sm shadow-2xl"
              onClick={e => e?.stopPropagation()}
            >
              <h3 className="text-sm font-bold text-foreground mb-3">Adherence Note</h3>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e?.target?.value)}
                placeholder="How did this exercise feel? Any modifications?"
                rows={3}
                className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                autoFocus
              />
              <div className="flex gap-2 mt-3">
                <button onClick={() => setShowNoteModal(false)} className="flex-1 py-2 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted transition-all">Cancel</button>
                <button onClick={saveNote} className="flex-1 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-all">Save Note</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Nutrition Day Card ───────────────────────────────────────────────────────
const NutritionDayCard = ({ day, completions, onToggle, planTargets }) => {
  const meals = day?.meals || [];
  const allItems = meals?.flatMap(m => m?.items || []);
  const completedCount = allItems?.filter(item => completions?.[item?.id]?.completed)?.length;
  const isToday = day?.plan_date === new Date()?.toISOString()?.split('T')?.[0];
  const isPast = new Date(day?.plan_date) < new Date(new Date()?.toISOString()?.split('T')?.[0]);

  const dayCalories = allItems?.reduce((acc, item) => {
    const food = item?.food_item || item?.custom_food_item;
    return acc + Math.round((food?.calories_per_serving || 0) * (item?.quantity || 1));
  }, 0);

  return (
    <div className={`bg-card border rounded-2xl overflow-hidden transition-all ${isToday ? 'border-orange-500/50 shadow-lg shadow-orange-500/10' : 'border-border'}`}>
      {/* Day Header */}
      <div className={`flex items-center justify-between px-4 py-3 ${isToday ? 'bg-orange-500/10' : 'bg-muted/30'}`}>
        <div className="flex items-center gap-2">
          {isToday && <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />}
          <div>
            <p className="text-sm font-bold text-foreground">
              {new Date(day?.plan_date + 'T12:00:00')?.toLocaleDateString('en-GB', { weekday: 'long' })}
            </p>
            <p className="text-xs text-muted-foreground">{new Date(day?.plan_date + 'T12:00:00')?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dayCalories > 0 && <span className="text-xs text-orange-400 font-semibold">{dayCalories} kcal</span>}
          {allItems?.length > 0 && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${completedCount === allItems?.length ? 'bg-success/20 text-success' : isPast ? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'}`}>
              {completedCount}/{allItems?.length}
            </span>
          )}
          {isToday && <span className="text-xs font-bold text-orange-500 bg-orange-500/10 px-2 py-0.5 rounded-full">Today</span>}
        </div>
      </div>

      {/* Meals */}
      <div className="p-3 space-y-3">
        {meals?.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">No meals planned</p>
        ) : (
          meals?.map(meal => {
            const items = meal?.items || [];
            if (items?.length === 0) return null;
            return (
              <div key={meal?.id}>
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">{meal?.name}</p>
                <div className="space-y-1.5">
                  {items?.map(item => {
                    const food = item?.food_item || item?.custom_food_item;
                    const done = completions?.[item?.id]?.completed;
                    const cals = Math.round((food?.calories_per_serving || 0) * (item?.quantity || 1));
                    return (
                      <div key={item?.id} className={`flex items-center gap-3 p-2 rounded-xl border transition-all ${done ? 'bg-success/5 border-success/20' : 'bg-background border-border'}`}>
                        <button
                          onClick={() => onToggle(item?.id, !done)}
                          className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${done ? 'bg-success border-success text-white' : 'border-border hover:border-orange-500'}`}
                        >
                          {done && <Icon name="Check" size={10} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-semibold truncate ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                            {food?.name || 'Food item'}
                          </p>
                          <p className="text-xs text-muted-foreground">{item?.quantity}× {food?.serving_size_description} · {cals} kcal</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ClientPlansPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('workout');
  const [isLoading, setIsLoading] = useState(true);

  // Workout plan state
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [workoutDays, setWorkoutDays] = useState([]);
  const [workoutCompletions, setWorkoutCompletions] = useState({});

  // Nutrition plan state
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [nutritionDays, setNutritionDays] = useState([]);
  const [nutritionCompletions, setNutritionCompletions] = useState({});

  // Week navigation
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    if (user?.id) loadAllPlans();
  }, [user?.id]);

  const loadAllPlans = async () => {
    setIsLoading(true);
    try {
      await Promise.all([loadWorkoutPlan(), loadNutritionPlan()]);
    } catch (err) {
      console.error('Load plans error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadWorkoutPlan = async () => {
    const { data: plans } = await supabase
      ?.from('client_workout_plans')
      ?.select('id, name, description, start_date, end_date, trainer:user_profiles!client_workout_plans_trainer_id_fkey(full_name)')
      ?.eq('client_id', user?.id)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: false })
      ?.limit(1);

    if (!plans?.length) return;
    const plan = plans?.[0];
    setWorkoutPlan(plan);

    const { data: days } = await supabase
      ?.from('client_workout_days')
      ?.select('id, scheduled_date, day_label, workout_plan_id')
      ?.eq('client_workout_plan_id', plan?.id)
      ?.order('scheduled_date', { ascending: true });

    if (!days?.length) return;

    const enriched = await Promise.all(
      days?.map(async (day) => {
        if (!day?.workout_plan_id) return { ...day, workoutPlanExercises: [] };
        const { data: exercises } = await supabase
          ?.from('workout_plan_exercises')
          ?.select('*, exercise:exercises(id, name, category, muscle_groups, equipment_needed)')
          ?.eq('workout_plan_id', day?.workout_plan_id)
          ?.order('order_index');
        return { ...day, workoutPlanExercises: exercises || [] };
      })
    );

    setWorkoutDays(enriched);

    // Load all completions
    const dayIds = enriched?.map(d => d?.id);
    if (dayIds?.length > 0) {
      const { data: comps } = await supabase
        ?.from('client_workout_completions')
        ?.select('*')
        ?.in('client_workout_day_id', dayIds)
        ?.eq('client_id', user?.id);

      const map = {};
      comps?.forEach(c => { map[c?.workout_plan_exercise_id] = c; });
      setWorkoutCompletions(map);
    }
  };

  const loadNutritionPlan = async () => {
    const { data: plans } = await supabase
      ?.from('nutrition_plans')
      ?.select('id, name, description, start_date, end_date, target_calories_per_day, target_protein_g_per_day, target_carbs_g_per_day, target_fats_g_per_day, trainer:user_profiles!nutrition_plans_trainer_id_fkey(full_name)')
      ?.eq('client_id', user?.id)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: false })
      ?.limit(1);

    if (!plans?.length) return;
    const plan = plans?.[0];
    setNutritionPlan(plan);

    const { data: days } = await supabase
      ?.from('nutrition_plan_days')
      ?.select('id, plan_date, target_calories, target_protein_g, target_carbs_g, target_fats_g')
      ?.eq('nutrition_plan_id', plan?.id)
      ?.order('plan_date', { ascending: true });

    if (!days?.length) return;

    const enriched = await Promise.all(
      days?.map(async (day) => {
        const { data: meals } = await supabase
          ?.from('nutrition_meals')
          ?.select('id, name, order_index')
          ?.eq('nutrition_plan_day_id', day?.id)
          ?.order('order_index');

        const mealsWithItems = await Promise.all(
          (meals || [])?.map(async (meal) => {
            const { data: items } = await supabase
              ?.from('nutrition_items')
              ?.select('id, quantity, food_item:food_items(id, name, serving_size_description, calories_per_serving, protein_g_per_serving, carbs_g_per_serving, fats_g_per_serving), custom_food_item:custom_food_items(id, name, serving_size_description, calories_per_serving)')
              ?.eq('nutrition_meal_id', meal?.id)
              ?.order('order_index');
            return { ...meal, items: items || [] };
          })
        );

        return { ...day, meals: mealsWithItems };
      })
    );

    setNutritionDays(enriched);

    // Load all nutrition completions
    const allItemIds = enriched?.flatMap(d => d?.meals?.flatMap(m => m?.items?.map(i => i?.id)));
    if (allItemIds?.length > 0) {
      const { data: comps } = await supabase
        ?.from('nutrition_item_completions')
        ?.select('*')
        ?.in('nutrition_item_id', allItemIds)
        ?.eq('client_id', user?.id);

      const map = {};
      comps?.forEach(c => { map[c?.nutrition_item_id] = c; });
      setNutritionCompletions(map);
    }
  };

  // ── Workout adherence handlers ──────────────────────────────────────────────
  const handleToggleWorkout = async (exerciseId, completed) => {
    const day = workoutDays?.find(d => d?.workoutPlanExercises?.some(ex => ex?.id === exerciseId));
    if (!day) return;
    const existing = workoutCompletions?.[exerciseId];
    try {
      if (existing) {
        await supabase?.from('client_workout_completions')?.update({ completed, completed_at: completed ? new Date()?.toISOString() : null })?.eq('id', existing?.id);
        setWorkoutCompletions(prev => ({ ...prev, [exerciseId]: { ...existing, completed, completed_at: completed ? new Date()?.toISOString() : null } }));
      } else {
        const { data } = await supabase?.from('client_workout_completions')?.insert({ client_workout_day_id: day?.id, workout_plan_exercise_id: exerciseId, client_id: user?.id, completed, completed_at: completed ? new Date()?.toISOString() : null })?.select()?.single();
        if (data) setWorkoutCompletions(prev => ({ ...prev, [exerciseId]: data }));
      }
    } catch (err) { console.error('Toggle workout error:', err); }
  };

  const handleEditWorkoutNote = async (exerciseId, notes) => {
    const existing = workoutCompletions?.[exerciseId];
    const day = workoutDays?.find(d => d?.workoutPlanExercises?.some(ex => ex?.id === exerciseId));
    if (!day) return;
    try {
      if (existing) {
        await supabase?.from('client_workout_completions')?.update({ notes })?.eq('id', existing?.id);
        setWorkoutCompletions(prev => ({ ...prev, [exerciseId]: { ...existing, notes } }));
      } else {
        const { data } = await supabase?.from('client_workout_completions')?.insert({ client_workout_day_id: day?.id, workout_plan_exercise_id: exerciseId, client_id: user?.id, completed: false, notes })?.select()?.single();
        if (data) setWorkoutCompletions(prev => ({ ...prev, [exerciseId]: data }));
      }
    } catch (err) { console.error('Edit note error:', err); }
  };

  // ── Nutrition adherence handlers ────────────────────────────────────────────
  const handleToggleNutrition = async (itemId, completed) => {
    const day = nutritionDays?.find(d => d?.meals?.some(m => m?.items?.some(i => i?.id === itemId)));
    const existing = nutritionCompletions?.[itemId];
    try {
      if (existing) {
        await supabase?.from('nutrition_item_completions')?.update({ completed, completed_at: completed ? new Date()?.toISOString() : null })?.eq('id', existing?.id);
        setNutritionCompletions(prev => ({ ...prev, [itemId]: { ...existing, completed, completed_at: completed ? new Date()?.toISOString() : null } }));
      } else {
        const { data } = await supabase?.from('nutrition_item_completions')?.insert({ nutrition_item_id: itemId, client_id: user?.id, completion_date: day?.plan_date || new Date()?.toISOString()?.split('T')?.[0], completed, completed_at: completed ? new Date()?.toISOString() : null })?.select()?.single();
        if (data) setNutritionCompletions(prev => ({ ...prev, [itemId]: data }));
      }
    } catch (err) { console.error('Toggle nutrition error:', err); }
  };

  // ── Week filtering ──────────────────────────────────────────────────────────
  const getWeekDays = (days, dateKey) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek?.setDate(today?.getDate() - today?.getDay() + 1 + weekOffset * 7);
    startOfWeek?.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek?.setDate(startOfWeek?.getDate() + 6);
    endOfWeek?.setHours(23, 59, 59, 999);
    return days?.filter(d => {
      const date = new Date(d?.[dateKey] + 'T12:00:00');
      return date >= startOfWeek && date <= endOfWeek;
    });
  };

  // Build a full 7-day week scaffold for workout tab, merging in actual days
  const getFullWeekWorkoutDays = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek?.setDate(today?.getDate() - today?.getDay() + 1 + weekOffset * 7);
    startOfWeek?.setHours(0, 0, 0, 0);

    return DAYS?.map((dayName, idx) => {
      const date = new Date(startOfWeek);
      date?.setDate(startOfWeek?.getDate() + idx);
      const dateStr = date?.toISOString()?.split('T')?.[0];

      // Find matching day from DB
      const match = workoutDays?.find(d => d?.scheduled_date === dateStr);

      if (match) {
        return { ...match, dayName, isRest: (match?.workoutPlanExercises?.length === 0) };
      }

      // No DB entry for this day → rest day scaffold
      return {
        id: `rest-${dateStr}`,
        dayName,
        scheduled_date: dateStr,
        isRest: true,
        workoutPlanExercises: [],
      };
    });
  };

  const weekWorkoutDays = getFullWeekWorkoutDays();
  const weekNutritionDays = getWeekDays(nutritionDays, 'plan_date');

  const getWeekLabel = () => {
    const today = new Date();
    const start = new Date(today);
    start?.setDate(today?.getDate() - today?.getDay() + 1 + weekOffset * 7);
    const end = new Date(start);
    end?.setDate(start?.getDate() + 6);
    if (weekOffset === 0) return 'This Week';
    if (weekOffset === -1) return 'Last Week';
    if (weekOffset === 1) return 'Next Week';
    return `${start?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${end?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  };

  // ── Adherence summary ───────────────────────────────────────────────────────
  const workoutAdherence = (() => {
    const total = weekWorkoutDays?.reduce((acc, d) => acc + (d?.workoutPlanExercises?.length || 0), 0);
    const done = weekWorkoutDays?.reduce((acc, d) => acc + (d?.workoutPlanExercises?.filter(ex => workoutCompletions?.[ex?.id]?.completed)?.length || 0), 0);
    return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
  })();

  const nutritionAdherence = (() => {
    const total = weekNutritionDays?.reduce((acc, d) => acc + d?.meals?.reduce((a, m) => a + (m?.items?.length || 0), 0), 0);
    const done = weekNutritionDays?.reduce((acc, d) => acc + d?.meals?.reduce((a, m) => a + (m?.items?.filter(i => nutritionCompletions?.[i?.id]?.completed)?.length || 0), 0), 0);
    return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
  })();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground text-sm">Loading your plans...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Plans – Form Forge</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 max-w-5xl">

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">My Plans</h1>
              <p className="text-sm text-muted-foreground mt-1">Trainer-assigned workout & nutrition plans</p>
            </div>
            {/* Week Navigator */}
            <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-2">
              <button onClick={() => setWeekOffset(w => w - 1)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                <Icon name="ChevronLeft" size={16} />
              </button>
              <span className="text-sm font-semibold text-foreground min-w-[90px] text-center">{getWeekLabel()}</span>
              <button onClick={() => setWeekOffset(w => w + 1)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                <Icon name="ChevronRight" size={16} />
              </button>
              {weekOffset !== 0 && (
                <button onClick={() => setWeekOffset(0)} className="text-xs text-primary hover:underline ml-1">Today</button>
              )}
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex gap-1 mb-6 p-1 bg-muted rounded-xl w-full sm:w-auto sm:inline-flex">
            <button
              onClick={() => setActiveTab('workout')}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'workout' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Icon name="Dumbbell" size={16} />
              Workout Plan
              {workoutPlan && <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${workoutAdherence?.pct >= 80 ? 'bg-success/20 text-success' : 'bg-primary/20 text-primary'}`}>{workoutAdherence?.pct}%</span>}
            </button>
            <button
              onClick={() => setActiveTab('nutrition')}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'nutrition' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Icon name="UtensilsCrossed" size={16} />
              Nutrition Plan
              {nutritionPlan && <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${nutritionAdherence?.pct >= 80 ? 'bg-success/20 text-success' : 'bg-orange-500/20 text-orange-400'}`}>{nutritionAdherence?.pct}%</span>}
            </button>
          </div>

          {/* ── WORKOUT TAB ── */}
          <AnimatePresence mode="wait">
            {activeTab === 'workout' && (
              <motion.div key="workout" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                {!workoutPlan ? (
                  <div className="text-center py-20 bg-card border border-border rounded-2xl">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon name="Dumbbell" size={28} className="text-primary" />
                    </div>
                    <h2 className="text-lg font-bold text-foreground mb-2">No Workout Plan Yet</h2>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">Your trainer hasn't assigned a workout plan yet. Check back soon!</p>
                  </div>
                ) : (
                  <>
                    {/* Plan Info Banner */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-primary/5 border border-primary/20 rounded-2xl mb-5">
                      <div>
                        <p className="text-sm font-bold text-foreground">{workoutPlan?.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {workoutPlan?.trainer?.full_name && `By ${workoutPlan?.trainer?.full_name} · `}
                          {workoutPlan?.start_date && new Date(workoutPlan?.start_date + 'T12:00:00')?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          {workoutPlan?.end_date && ` – ${new Date(workoutPlan?.end_date + 'T12:00:00')?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <p className="text-lg font-bold text-primary">{workoutAdherence?.done}/{workoutAdherence?.total}</p>
                          <p className="text-xs text-muted-foreground">This week</p>
                        </div>
                        <div className="w-12 h-12 relative">
                          <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                            <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="3" className="text-primary/20" />
                            <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={`${workoutAdherence?.pct * 0.942} 94.2`} className="text-primary transition-all duration-500" />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-primary">{workoutAdherence?.pct}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Days Grid */}
                    {weekWorkoutDays?.length === 0 ? (
                      <div className="text-center py-12 bg-card border border-border rounded-2xl">
                        <Icon name="Calendar" size={32} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">No workouts scheduled for this week</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {weekWorkoutDays?.map(day => (
                          <WorkoutDayCard
                            key={day?.id}
                            day={day}
                            completions={workoutCompletions}
                            onToggle={handleToggleWorkout}
                            onEditNote={handleEditWorkoutNote}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {/* ── NUTRITION TAB ── */}
            {activeTab === 'nutrition' && (
              <motion.div key="nutrition" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                {!nutritionPlan ? (
                  <div className="text-center py-20 bg-card border border-border rounded-2xl">
                    <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
                      <Icon name="UtensilsCrossed" size={28} className="text-orange-500" />
                    </div>
                    <h2 className="text-lg font-bold text-foreground mb-2">No Nutrition Plan Yet</h2>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">Your trainer hasn't assigned a nutrition plan yet. Check back soon!</p>
                  </div>
                ) : (
                  <>
                    {/* Plan Info Banner */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-orange-500/5 border border-orange-500/20 rounded-2xl mb-5">
                      <div>
                        <p className="text-sm font-bold text-foreground">{nutritionPlan?.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {nutritionPlan?.trainer?.full_name && `By ${nutritionPlan?.trainer?.full_name} · `}
                          {nutritionPlan?.start_date && new Date(nutritionPlan?.start_date + 'T12:00:00')?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          {nutritionPlan?.end_date && ` – ${new Date(nutritionPlan?.end_date + 'T12:00:00')?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                        </p>
                        {/* Macro targets */}
                        {nutritionPlan?.target_calories_per_day > 0 && (
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-orange-400 font-semibold">{nutritionPlan?.target_calories_per_day} kcal</span>
                            <span className="text-xs text-blue-400">{nutritionPlan?.target_protein_g_per_day}g protein</span>
                            <span className="text-xs text-yellow-400">{nutritionPlan?.target_carbs_g_per_day}g carbs</span>
                            <span className="text-xs text-orange-300">{nutritionPlan?.target_fats_g_per_day}g fats</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <p className="text-lg font-bold text-orange-500">{nutritionAdherence?.done}/{nutritionAdherence?.total}</p>
                          <p className="text-xs text-muted-foreground">This week</p>
                        </div>
                        <div className="w-12 h-12 relative">
                          <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                            <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="3" className="text-orange-500/20" />
                            <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={`${nutritionAdherence?.pct * 0.942} 94.2`} className="text-orange-500 transition-all duration-500" />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-orange-500">{nutritionAdherence?.pct}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Days Grid */}
                    {weekNutritionDays?.length === 0 ? (
                      <div className="text-center py-12 bg-card border border-border rounded-2xl">
                        <Icon name="Calendar" size={32} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">No meals planned for this week</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {weekNutritionDays?.map(day => (
                          <NutritionDayCard
                            key={day?.id}
                            day={day}
                            completions={nutritionCompletions}
                            onToggle={handleToggleNutrition}
                            planTargets={nutritionPlan}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </>
  );
};

export default ClientPlansPage;
